// CattleSendToProcessorModal — reworked for the Cattle Forecast lane.
// ---------------------------------------------------------------------------
// Pops up when a cattle finisher session is being completed and at least
// one entry has send_to_processor=true.
//
// Choose an existing booking, or use the next forecast suggestion. Bookings
// do not require a forecast roster; only the selected weigh-in entries attach.
import React from 'react';
import type {CSSProperties} from 'react';
import {useAuth} from '../contexts/AuthContext.jsx';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {ForecastResult, NextProcessorBatch} from '../lib/cattleForecast.js';
import type {CattleRow, CattleProcessingBatchRow, WeighInEntryRow} from '../lib/cattleProcessingBatch.js';
import {CattleProcessingAttachError} from '../lib/cattleProcessingBatch.js';
import {attachCattleToProcessingBatch} from '../lib/processingAttachApi.js';
import {buildForecast, checkProcessorGate} from '../lib/cattleForecast.js';
import {
  loadCattleProcessingBatchRows,
  loadForecastSettings,
  loadHeiferIncludes,
  loadHidden,
  loadSendForecastWeighIns,
} from '../lib/cattleForecastApi.js';
import type {SendForecastWeighInRow} from '../lib/cattleForecastApi.js';
import {recordSaveButton, recordSecondaryButton} from '../shared/recordPageControls.jsx';
import {buttonDisabled} from '../lib/buttonStyles.js';

type SendSession = {id?: string | null; date?: string | null};
type SendForecast = ForecastResult & {
  _realBatches?: CattleProcessingBatchRow[];
  _scheduledBatches?: CattleProcessingBatchRow[];
};
type CattleSendModalProps = {
  sb: SupabaseClient;
  session: SendSession | null | undefined;
  flaggedEntries: WeighInEntryRow[];
  cattleList: unknown[];
  // The forecast's weigh-in history. The record page passes the full cattle
  // history it already holds. A caller that holds only the open session's rows
  // (/weighins) omits it and passes those rows as sessionEntries: the modal then
  // loads the history itself (loadSendForecastWeighIns), fail-closed.
  weighIns?: WeighInEntryRow[];
  sessionEntries?: SendForecastWeighInRow[];
  teamMember?: string | null;
  onCancel: () => void;
  onFailure?: (message: string) => void;
  onConfirmed: (result: {batch: CattleProcessingBatchRow; attached: unknown[]; skipped: unknown[]}) => void;
};

export default function CattleSendToProcessorModal({
  sb,
  session,
  flaggedEntries,
  cattleList,
  weighIns,
  sessionEntries,
  teamMember,
  onCancel,
  onFailure,
  onConfirmed,
}: CattleSendModalProps) {
  // The role is read HERE, as SheepSendToProcessorModal does, rather than
  // taken as a prop. The /weighins presenter in src/webforms is held to an
  // identity-only contract by WeighInsAuthenticatedBridge and cannot hand a
  // role through; it used to pass `authState={null}`, which left Send disabled
  // for everyone, admins included. This decides only what the modal OFFERS:
  // the server re-derives the caller's role before it writes.
  const {authState} = useAuth() as {authState: {role?: string | null} | null | false};
  const role = authState ? authState.role : null;
  const canSend = role === 'admin' || role === 'management';
  const [loading, setLoading] = React.useState(true);
  const [forecast, setForecast] = React.useState<SendForecast | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [sendFailed, setSendFailed] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [gateBlocked, setGateBlocked] = React.useState<string[]>([]);
  const [selectedBookingId, setSelectedBookingId] = React.useState('');
  const sessionDate = session?.date || new Date().toISOString().slice(0, 10);
  const [sentDate, setSentDate] = React.useState(sessionDate);
  const cattleRows = cattleList as CattleRow[];
  const sessionId = session?.id || null;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settings, includes, hidden, batchRows, history] = await Promise.all([
          loadForecastSettings(sb),
          loadHeiferIncludes(sb),
          loadHidden(sb),
          // Soft-fails to [] exactly as the inline `batchesR.data || []` did —
          // see the loader's own note on why that is preserved rather than
          // corrected in a move.
          loadCattleProcessingBatchRows(sb),
          // Throws on a failed read (no DOB-only fallback forecast).
          weighIns ? Promise.resolve(weighIns) : loadSendForecastWeighIns(sb, {id: sessionId, entries: sessionEntries}),
        ]);
        if (cancelled) return;
        // The same cast the inline `(batchesR.data || []) as
        // CattleProcessingBatchRow[]` made. Two modules declare a row type of
        // that name — cattleForecast's (whose `cows_detail` is `unknown`,
        // because the forecast never reads into it) and cattleProcessingBatch's
        // (which does) — and the attach path below needs the latter.
        const allBatches = batchRows as CattleProcessingBatchRow[];
        const realBatches = allBatches.filter((b) => b.status === 'active' || b.status === 'complete');
        const scheduledBatches = allBatches.filter((b) => b.status === 'scheduled');
        const f = buildForecast({
          cattle: cattleRows,
          weighIns: history,
          settings,
          includes,
          hidden,
          realBatches,
          scheduledBatches,
          todayMs: Date.now(),
        });
        // Stash all batch rows on the forecast so the promote path can
        // grab the underlying scheduled row when promoting.
        setForecast({...f, _realBatches: realBatches, _scheduledBatches: scheduledBatches});
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setErr('Could not load forecast: ' + (e instanceof Error ? e.message : String(e)));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sb, cattleList, weighIns, sessionEntries, sessionId]);

  const totalWeight = flaggedEntries.reduce((s, e) => s + (parseFloat(e.weight as string) || 0), 0);
  const selectedTags = flaggedEntries.map((e) => e.tag).filter(Boolean) as string[];
  const selectedBooking = forecast?.scheduledBatches.find((booking) => booking.id === selectedBookingId);
  const next: NextProcessorBatch | null = selectedBookingId
    ? selectedBooking
      ? {
          ...selectedBooking,
          source: 'scheduled',
          scheduledId: selectedBooking.id || null,
          allowedTagSet: new Set(
            cattleRows
              .filter((cow) => selectedBooking.animalIds.includes(cow.id) && cow.tag)
              .map((cow) => String(cow.tag)),
          ),
          currentYearTotalForecast: forecast?.summary.readyThisYear || 0,
        }
      : null
    : forecast?.nextProcessorBatch || null;
  const sentDateMs = Date.parse(sentDate + 'T00:00:00Z');
  const sentDateValid =
    /^\d{4}-\d{2}-\d{2}$/.test(sentDate) &&
    Number.isFinite(sentDateMs) &&
    new Date(sentDateMs).toISOString().slice(0, 10) === sentDate;
  const bookedDate =
    next?.source === 'scheduled'
      ? forecast?.scheduledBatches.find((booking) => booking.id === next.scheduledId)?.planned_process_date
      : null;
  const gate = next
    ? checkProcessorGate({selectedTags, nextProcessorBatch: next})
    : {ok: false, reason: 'no_next_batch', blockedTags: []};

  // Codex 2026-05-12 correction: outside-projection tags are a WARNING
  // when there's a valid next batch (scheduled or virtual). Ronnie can
  // intentionally send a cow not in the projection or skip a projected
  // cow; only the actually-sent entries move to processed via
  // the audited attach RPC. Hard-block is reserved for true no-batch cases.
  // A saved booking remains a valid target even without forecast cattle.
  const hasNextBatch = !!next;
  const outsideTagsWarning =
    hasNextBatch && !gate.ok && gate.reason === 'tags_outside_next_batch' ? gate.blockedTags : [];
  const hardBlocked = !hasNextBatch || (!gate.ok && gate.reason !== 'tags_outside_next_batch');
  const confirmDisabled = busy || sendFailed || loading || hardBlocked || !canSend || !sentDateValid;

  // Surface the outside-projection tags in real-time (no Submit needed).
  React.useEffect(() => {
    setGateBlocked(outsideTagsWarning);
  }, [outsideTagsWarning.join(',')]);

  async function go() {
    if (busy || sendFailed || loading) return;
    setErr('');
    if (!canSend) {
      setErr('Send-to-Processor is restricted to management/admin.');
      return;
    }
    if (hardBlocked) {
      setErr(
        gate.reason === 'no_next_batch'
          ? 'Choose a scheduled booking or a planned batch — there are no cattle eligible for processing yet.'
          : gate.reason === 'empty_next_batch'
            ? 'The next planned batch is empty.'
            : 'Cannot send: forecast has no valid next batch yet.',
      );
      return;
    }
    if (!sentDateValid) {
      setErr('Enter the date the cattle were sent to the processor.');
      return;
    }
    setBusy(true);
    try {
      // Migration 240: the audited attach RPC is the ONLY path. It promotes a
      // scheduled row (or creates the batch when none matches), appends
      // cows_detail, moves the herd, stamps the weigh-ins, writes the
      // transfers and the Activity — atomically. The legacy client-helper
      // fallback (the whole-array cows_detail append and its per-cow moves)
      // is deleted with migration 240: both callers
      // hold real weigh-in sessions, and that client-side append
      // was the same whole-array write the weight grid's operation replaces.
      if (next.source === 'scheduled' && next.scheduledId) {
        const scheduledRow = (forecast?._scheduledBatches || []).find((b) => b.id === next.scheduledId);
        if (!scheduledRow) {
          throw new Error('Scheduled batch ' + next.scheduledId + ' missing from forecast.');
        }
      }
      const result = await attachCattleToProcessingBatch(sb, {
        sessionId: session?.id as string | undefined,
        entryIds: flaggedEntries.map((e) => e.id).filter(Boolean),
        targetBatchId: next.source === 'scheduled' ? next.scheduledId : null,
        batchName: next.source === 'scheduled' ? null : next.name,
        processingDate: sentDate,
        teamMember,
      });
      const batch = result.batch as CattleProcessingBatchRow;
      const attached = (result.attached as unknown[]) || [];
      const skipped = (result.skipped as unknown[]) || [];
      onConfirmed({batch, attached, skipped});
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e) || 'Could not attach to batch.';
      const failureMessage =
        e instanceof CattleProcessingAttachError
          ? message
          : 'Send stopped: ' +
            message +
            ' The batch may already have been created or changed. Do not retry until you review it.';
      setErr(failureMessage);
      onFailure?.(failureMessage);
      setSendFailed(true);
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        zIndex: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      data-cattle-send-modal
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-modal)',
          maxWidth: 540,
          maxHeight: 'calc(100dvh - 32px)',
          overflowY: 'auto',
          width: '100%',
          padding: '18px 20px',
          boxShadow: 'var(--shadow-modal)',
        }}
      >
        <div style={{fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6}}>
          {'🚩 Send ' +
            flaggedEntries.length +
            ' finisher' +
            (flaggedEntries.length === 1 ? '' : 's') +
            ' to processor'}
        </div>
        {/* Constitution P01/F03: a role that can never send gets one gray
            View-only line, the values as plain text, and Cancel -- no booking
            or date controls and no Send button. */}
        {!canSend && (
          <div
            data-cattle-send-handoff
            style={{fontSize: 12, color: 'var(--ink-muted)', lineHeight: 1.5, marginBottom: 6}}
          >
            View only — sending cattle to the processor is done by management. Your weights and the{' '}
            {flaggedEntries.length} flagged {flaggedEntries.length === 1 ? 'cow' : 'cattle'} are saved; leave this
            session as a draft so management can complete the send from Weigh-In Sessions.
          </div>
        )}
        <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10}}>
          {'Total live weight: ' + Math.round(totalWeight).toLocaleString() + ' lb · session date ' + sessionDate}
        </div>

        {loading && (
          <div style={{padding: '20px 0', textAlign: 'center', color: 'var(--ink-faint)', fontSize: 12}}>
            Loading forecast{'…'}
          </div>
        )}

        {canSend && !loading && !!forecast?.scheduledBatches.length && (
          <label style={{display: 'block', marginBottom: 12}}>
            Processing booking
            <select
              value={selectedBookingId}
              onChange={(e) => setSelectedBookingId(e.target.value)}
              disabled={busy}
              data-cattle-processing-booking
              style={{display: 'block', width: '100%', marginTop: 4}}
            >
              <option value="">
                Suggested next batch{forecast.nextProcessorBatch ? ' - ' + forecast.nextProcessorBatch.name : ''}
              </option>
              {forecast.scheduledBatches.map((booking) => (
                <option key={booking.id} value={booking.id || ''}>
                  {booking.name} - {booking.planned_process_date || 'Date not recorded'}
                </option>
              ))}
            </select>
          </label>
        )}

        {!loading && next && (
          <div
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 10,
              background: 'var(--bg-card)',
              marginBottom: 12,
            }}
            data-send-modal-next-batch
          >
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-muted)',
                textTransform: 'uppercase',
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {next.source === 'scheduled' ? 'Scheduled booking' : 'Next forecast batch'}
            </div>
            <div style={{fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginTop: 2}}>{next.name}</div>
            <div style={{fontSize: 11, color: 'var(--ink-muted)', marginTop: 2}}>
              {next.label} · {next.animalIds.length} {next.animalIds.length === 1 ? 'cow' : 'cows'} eligible
            </div>
          </div>
        )}

        {canSend && !loading && !next && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--warn-soft)',
              border: '1px solid var(--border)',
              color: 'var(--warn-ink)',
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            No planned batch from the Forecast — no eligible cattle land in the display window. Add weights or DOB to
            bring cattle into the forecast first.
          </div>
        )}

        {canSend && !loading && gateBlocked.length > 0 && (
          <div
            data-send-modal-outside-tags
            data-send-modal-outside-reason="outside_projection"
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--warn-soft)',
              border: '1px solid var(--border)',
              color: 'var(--warn-ink)',
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            <div style={{fontWeight: 700, marginBottom: 4}}>Heads up: tags outside the projected cohort</div>
            <div style={{display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6}}>
              {gateBlocked.map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: 'var(--surface)',
                    color: 'var(--warn-ink)',
                    border: '1px solid var(--border)',
                  }}
                >
                  #{t}
                </span>
              ))}
            </div>
            <div style={{fontSize: 11, color: 'var(--warn-ink)'}}>
              These tags aren&apos;t in the next batch&apos;s projected cohort. The actual cattle you send override the
              projection — only the selected entries move to processed. Confirm if you intend to send them anyway.
            </div>
          </div>
        )}

        {canSend && !loading && next && !hardBlocked && (
          <div style={{marginBottom: 12}}>
            <label style={{display: 'block'}}>
              Sent to processor date
              <input
                type="date"
                value={sentDate}
                onChange={(e) => setSentDate(e.target.value)}
                disabled={busy}
                required
                max="9999-12-31"
                data-cattle-sent-date
                aria-describedby="cattle-sent-date-help"
                aria-invalid={!sentDateValid}
                style={{display: 'block', width: '100%', marginTop: 4}}
              />
            </label>
            <div id="cattle-sent-date-help" style={{fontSize: 11, color: 'var(--ink-muted)', marginTop: 4}}>
              Starts with the weigh-in date. Change it if the cattle were sent on another day.
              {bookedDate && (
                <>
                  {' '}
                  The booked date for {next.name} stays {bookedDate}.
                </>
              )}
            </div>
            {!sentDateValid && (
              <div style={{fontSize: 12, color: 'var(--danger-ink)', marginTop: 4}}>
                Enter the date the cattle were sent to the processor.
              </div>
            )}
          </div>
        )}

        {canSend && !loading && next && !hardBlocked && sentDateValid && (
          <div style={{fontSize: 12, color: 'var(--ink)', marginBottom: 12}}>
            This will {next.source === 'scheduled' ? 'promote scheduled batch' : 'create active batch'}{' '}
            <strong>{next.name}</strong> and send <strong>{flaggedEntries.length}</strong>{' '}
            {flaggedEntries.length === 1 ? 'cow' : 'cattle'} to processor. Sent to processor date:{' '}
            <strong>{sentDate}</strong>. Weigh-in date stays <strong>{sessionDate}</strong>.
          </div>
        )}

        {err && (
          <div
            style={{
              color: 'var(--danger-ink)',
              fontSize: 12,
              marginBottom: 10,
              padding: '6px 10px',
              background: 'var(--danger-soft)',
              borderRadius: 10,
            }}
          >
            {err}
          </div>
        )}

        <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              ...recordSecondaryButton,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          {canSend && (
            <button
              onClick={go}
              disabled={confirmDisabled}
              data-send-modal-confirm
              style={{
                ...recordSaveButton,
                ...(confirmDisabled ? buttonDisabled : null),
              }}
            >
              {busy ? 'Attaching…' : sendFailed ? 'Review required' : 'Send to processor'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
